import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTimes,
  faBell,
  faListCheck,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import reminderService, { ReminderPayload } from '../../services/reminderService';
import taskService, { TaskPayload } from '../../services/taskService';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import './QuickAddFAB.css';

type QuickTab = 'reminder' | 'task';

const QuickAddFAB: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<QuickTab>('reminder');
  const [title, setTitle] = useState('');
  const [datetime, setDatetime] = useState('');
  const [priority, setPriority] = useState('medium');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { canAccess } = useServiceAccess();
  const remindersAllowed = canAccess('medicine-reminder');

  // Fall back to the Task tab when the medicine-reminder service is disabled
  useEffect(() => {
    if (!remindersAllowed && activeTab === 'reminder') setActiveTab('task');
  }, [remindersAllowed, activeTab]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  const resetForm = () => {
    setTitle('');
    setDatetime('');
    setPriority('medium');
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    if (!datetime) return;
    if (activeTab === 'reminder' && !remindersAllowed) return;

    try {
      setSaving(true);
      const reminder_datetime = new Date(datetime).toISOString();
      if (activeTab === 'task') {
        const taskPayload: TaskPayload = {
          title: title.trim(),
          reminder_datetime,
          priority,
          recurrence: 'one_time',
        };
        await taskService.createTask(taskPayload);
        setToast('Task created!');
      } else {
        const reminderPayload: ReminderPayload = {
          title: title.trim(),
          reminder_datetime,
          priority,
          category: 'personal',
          recurrence: 'one_time',
        };
        await reminderService.createReminder(reminderPayload);
        setToast('Reminder created!');
      }
      resetForm();
      setOpen(false);
      // Notify other components to refresh their reminder/task lists
      window.dispatchEvent(new Event('reminders-updated'));
    } catch {
      setToast('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="qaf-toast">
          <FontAwesomeIcon icon={faCheck} />
          {toast}
        </div>
      )}

      {/* FAB + Popover wrapper */}
      <div className="qaf-container" ref={popoverRef}>
        {/* Popover */}
        {open && (
          <div className="qaf-popover">
            {/* Tabs */}
            <div className="qaf-tabs">
              {remindersAllowed && (
                <button
                  className={`qaf-tab ${activeTab === 'reminder' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reminder')}
                >
                  <FontAwesomeIcon icon={faBell} />
                  Reminder
                </button>
              )}
              <button
                className={`qaf-tab ${activeTab === 'task' ? 'active' : ''}`}
                onClick={() => setActiveTab('task')}
              >
                <FontAwesomeIcon icon={faListCheck} />
                Task
              </button>
            </div>

            {/* Form */}
            <div className="qaf-form">
              <input
                type="text"
                className="qaf-input"
                placeholder={activeTab === 'task' ? 'Task title...' : 'Reminder title...'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
              <input
                type="datetime-local"
                className="qaf-input"
                value={datetime}
                onChange={(e) => setDatetime(e.target.value)}
              />
              <div className="qaf-priority-row">
                {(['low', 'medium', 'high'] as const).map((p) => (
                  <button
                    key={p}
                    className={`qaf-priority-btn qaf-p-${p} ${priority === p ? 'active' : ''}`}
                    onClick={() => setPriority(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                className="qaf-submit"
                onClick={handleSubmit}
                disabled={saving || !title.trim() || !datetime}
              >
                {saving ? 'Saving...' : activeTab === 'task' ? 'Add Task' : 'Add Reminder'}
              </button>
            </div>
          </div>
        )}

        {/* FAB Button */}
        <button
          className={`qaf-fab ${open ? 'open' : ''}`}
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close quick add' : 'Quick add reminder or task'}
        >
          <FontAwesomeIcon icon={open ? faTimes : faPlus} />
        </button>
      </div>
    </>
  );
};

export default QuickAddFAB;
